import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import NewAdminLoginPage from './pages/NewAdminLoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DeleteAdminPage from './pages/DeleteAdminPage';
import EmailConfigPage from './pages/EmailConfigPage';
import SpeedPage from './pages/SpeedPage';
import PitchPage from './pages/PitchPage';
import ContactPage from './pages/ContactPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import NewAdminPage from './pages/NewAdminPage';
import SubscriptionPage from './pages/SubscriptionPage';
import Layout from './components/Layout';
import StorageWarning from './components/StorageWarning';
import SyncStatus from './components/SyncStatus';
import DataLimitWarning from './components/DataLimitWarning';
import SubscriptionStatus from './components/SubscriptionStatus';
import { useAuthStore } from './stores/authStore';
import { useAnalysisDataStore } from './stores/analysisDataStore';
import { extractAccessTokenFromUrl } from './lib/supabase';
import ResetPasswordCallback from './pages/reset-password-callback';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  requireSubscription?: boolean;
}

function ProtectedRoute({ children, adminOnly = false, requireSubscription = false }: ProtectedRouteProps) {
  const { isAuthenticated, isAdmin, hasSubscription } = useAuthStore();
  const location = useLocation();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireSubscription && !hasSubscription && !isAdmin) {
    return <Navigate to="/subscription" replace />;
  }

  return <Layout>{children}</Layout>;
}

function App() {
  const { isAuthenticated, checkSession } = useAuthStore();
  const { fetchResults, checkLimit } = useAnalysisDataStore();
  const location = useLocation();

  useEffect(() => {
    const initializeAuth = async () => {
      await checkSession();
      if (isAuthenticated) {
        await Promise.all([fetchResults(), checkLimit()]);
      }
    };
    
    initializeAuth();
  }, [checkSession, isAuthenticated, fetchResults, checkLimit]);

  useEffect(() => {
    console.log('Current path:', location.pathname, 'Search:', location.search, 'Hash:', location.hash);
    
    if (window.location.href.includes('access_token=')) {
      console.log('Access token detected in URL');
      
      const token = extractAccessTokenFromUrl();
      if (token) {
        console.log('Successfully extracted token from URL');
        
        try {
          localStorage.setItem('auth_access_token', token);
          localStorage.setItem('auth_token_stored_at', new Date().toISOString());
        } catch (e) {
          console.error('Failed to store access token:', e);
        }
      }
    }
  }, [location]);

  useEffect(() => {
    const fullUrl = window.location.href;
    const isResetUrl = 
      fullUrl.includes('access_token=') || 
      fullUrl.includes('type=recovery') || 
      fullUrl.includes('type=reset_password');
    
    if (isResetUrl) {
      console.log('Auth parameters detected in URL, preventing navigation away from reset page');
      
      try {
        localStorage.setItem('auth_recovery_url', fullUrl);
        localStorage.setItem('auth_recovery_app_time', new Date().toISOString());
        
        const token = extractAccessTokenFromUrl();
        if (token) {
          localStorage.setItem('auth_access_token', token);
          console.log('Stored access token in App component');
        }
      } catch (e) {
        console.error('Failed to store auth recovery URL:', e);
      }
      
      const isOnResetPage = location.pathname.includes('/reset-password');
      
      if (!isOnResetPage) {
        console.log('Redirecting to reset password page from App component');
        
        // 直接リダイレクト
        window.location.href = '/reset-password?type=recovery';
      }
    }
  }, [location]);

  return (
    <div className="h-full">
      <Routes>
        <Route 
          path="/" 
          element={
            isAuthenticated ? (
              <ProtectedRoute requireSubscription={true}>
                <DashboardPage />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin-login" element={<AdminLoginPage />} />
        <Route path="/new-admin-login" element={<NewAdminLoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/delete-admin" element={<DeleteAdminPage />} />
        <Route path="/email-config" element={<EmailConfigPage />} />
        <Route path="/reset-password-callback" element={<ResetPasswordCallback />} />

        {/* Protected Routes */}
        <Route
          path="/speed"
          element={
            <ProtectedRoute requireSubscription={true}>
              <SpeedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pitch"
          element={
            <ProtectedRoute requireSubscription={true}>
              <PitchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact"
          element={
            <ProtectedRoute requireSubscription={true}>
              <ContactPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute requireSubscription={true}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/new-admin" 
          element={
            <ProtectedRoute adminOnly={true}>
              <NewAdminPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Status Components */}
      {isAuthenticated && (
        <>
          <StorageWarning />
          <SyncStatus />
          <DataLimitWarning />
          <SubscriptionStatus />
        </>
      )}
    </div>
  );
}

export default App;